import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './NavbarProfileSelector.module.css';
import { EnrichedProfile } from './ProfileCard';
import { IconUser, IconPlus, IconCalendar, IconMapPin, IconChevronDown } from '@tabler/icons-react';

interface NavbarProfileSelectorProps {
    profiles: EnrichedProfile[];
    selectedProfileId: string | null;
    onProfileSelect: (profile: EnrichedProfile) => void;
    onAddProfileClick: (e: React.MouseEvent) => void;
}

export default function NavbarProfileSelector({
    profiles,
    selectedProfileId,
    onProfileSelect,
    onAddProfileClick,
}: NavbarProfileSelectorProps) {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedProfile = profiles.find(p => p.id === selectedProfileId);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleSelect = (profile: EnrichedProfile) => {
        onProfileSelect(profile);
        setDropdownOpen(false);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    // If no profiles, Show simple add button
    if (profiles.length === 0) {
        return (
            <button
                className={styles.triggerButton}
                onClick={(e) => onAddProfileClick(e)}
                aria-label="Create Profile"
            >
                <div className={styles.profileAvatar} style={{ background: 'linear-gradient(135deg, #ff3e9f 0%, #fe450b 100%)', border: 'none' }}>
                    <IconPlus size={18} />
                </div>
                <span className={`${styles.profileName} d-none d-md-block`}>Create Profile</span>
            </button>
        );
    }

    return (
        <div className={styles.container} ref={dropdownRef}>
            <button
                className={styles.triggerButton}
                onClick={() => setDropdownOpen(!isDropdownOpen)}
                aria-label="Select Profile"
            >
                <div className={styles.profileAvatar}>
                    {selectedProfile?.name ? selectedProfile.name.charAt(0).toUpperCase() : <IconUser size={18} />}
                </div>

                {selectedProfile && (
                    <div className={styles.profileInfo}>
                        <span className={styles.profileName}>{selectedProfile.name}</span>
                    </div>
                )}

                <IconChevronDown
                    size={16}
                    className={`${styles.chevron} ${isDropdownOpen ? styles.open : ''}`}
                />
            </button>

            {isDropdownOpen && (
                <div className={styles.dropdownPanel}>
                    <Link
                        to="/profiles/new"
                        className={styles.addProfileLink}
                        onClick={(e) => {
                            setDropdownOpen(false);
                            onAddProfileClick(e);
                        }}
                    >
                        <IconPlus size={18} />
                        <span>Add New Profile</span>
                    </Link>

                    <div className={styles.separator}></div>

                    <div className={styles.profilesList}>
                        {profiles.map(profile => (
                            <button
                                key={profile.id}
                                className={`${styles.dropdownItem} ${selectedProfileId === profile.id ? styles.active : ''}`}
                                onClick={() => handleSelect(profile)}
                            >
                                <div className={styles.profileAvatar} style={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                                    {profile.name.charAt(0).toUpperCase()}
                                </div>
                                <div className={styles.itemContent}>
                                    <span className={styles.itemName}>{profile.name}</span>
                                    <div className={styles.itemDetails}>
                                        <span className={styles.detailItem}>{profile.gender}</span>
                                        <span className={styles.separatorDot}>•</span>
                                        <span className={styles.detailItem}>{formatDate(profile.date_of_birth)}</span>
                                        <span className={styles.separatorDot}>•</span>
                                        <span className={styles.detailItem}>{profile.time_of_birth}</span>
                                    </div>
                                    <div className={styles.itemSubDetails}>
                                        <span className={styles.detailItem}><IconMapPin size={10} /> {profile.birth_place}</span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
